import { disableMfa, enableMFA, verifyMFAToken } from "@actions/auth";
import { getUserInfo } from "@actions/profile";
import { AuthButtonSpinner } from "@layouts/betto/authPopup/components/AuthModal/elements.js";
import { getUser } from "@utils";
import React, { memo, useCallback, useState } from "react";
import PropTypes from "prop-types";
import { connect } from "react-redux";
import { BettoPrimaryButton } from "../BettoButton";
import { BettoMfaModal } from "./BettoMfaModal";
import { MfaDescription, MfaRoot, MfaStatus } from "./elements";

export const BettoTwoFactorAuth = memo(
	({ translate, enabled, enableMFA, disableMfa, verifyMFAToken, getUserInfo }) => {
		const [modalOpen, setModalOpen] = useState(false);
		const [mfaData, setMfaData] = useState({});
		const [isLoading, setIsLoading] = useState(false);

		const refreshUser = useCallback(() => {
			const user = getUser();
			if (user) getUserInfo();
		}, [getUserInfo]);

		const handleEnable = useCallback(() => {
			setIsLoading(true);
			enableMFA(
				(res) => {
					setMfaData(res.data);
					setModalOpen(true);
					setIsLoading(false);
				},
				() => setIsLoading(false)
			);
		}, [enableMFA]);

		const handleDisable = useCallback(() => {
			setIsLoading(true);
			disableMfa(
				() => {
					refreshUser();
					setIsLoading(false);
				},
				() => setIsLoading(false)
			);
		}, [disableMfa, refreshUser]);

		const handleCloseModal = useCallback(() => {
			setModalOpen(false);
			refreshUser();
		}, [refreshUser]);

		const handleVerify = useCallback(
			(values, onClose) => {
				verifyMFAToken(values, () => {
					onClose?.();
					refreshUser();
				});
			},
			[refreshUser, verifyMFAToken]
		);

		return (
			<MfaRoot>
				<MfaStatus $enabled={enabled}>
					{enabled
						? translate("TWO_FACTOR_AUTH_INFO_H31")
						: translate("TWO_FACTOR_AUTH_INFO_H3")}
				</MfaStatus>
				<MfaDescription>{translate("TWO_FACTOR_AUTH_INFO_P1")}</MfaDescription>
				<BettoPrimaryButton
					variant={enabled ? "secondary" : "primary"}
					onClick={enabled ? handleDisable : handleEnable}
					disabled={isLoading}>
					{isLoading ? <AuthButtonSpinner /> : enabled ? translate("DISABLE") : translate("ENABLE_2FA")}
				</BettoPrimaryButton>
				<BettoMfaModal
					translate={translate}
					data={mfaData}
					isOpen={modalOpen}
					onClose={handleCloseModal}
					onVerify={handleVerify}
				/>
			</MfaRoot>
		);
	}
);

BettoTwoFactorAuth.displayName = "BettoTwoFactorAuth";

BettoTwoFactorAuth.propTypes = {
	translate: PropTypes.func.isRequired,
	enabled: PropTypes.bool,
	enableMFA: PropTypes.func,
	disableMfa: PropTypes.func,
	verifyMFAToken: PropTypes.func,
	getUserInfo: PropTypes.func,
};

const mapDispatchToProps = { enableMFA, verifyMFAToken, disableMfa, getUserInfo };

export default connect(null, mapDispatchToProps)(BettoTwoFactorAuth);
